/**
 * Metadata Generator Service Contract
 * Handles AI-powered metadata generation and editing functionality
 */

import type { ImageFile } from './uploadService'

export interface Metadata {
  imageId: string
  title: string
  description: string
  keywords: string[]
  category: string
  isCommercial: boolean
  hasModelRelease: boolean
  hasPropertyRelease: boolean
  complianceFlags: string[]
  confidence: number
  isEdited: boolean
  generatedAt: Date
  editedAt?: Date
  analysis?: ImageAnalysis // AI analysis results for better export decisions
}

export interface ImageContext {
  location?: string
  title?: string
  date?: Date // Full date for precise editorial formatting
  month?: number // 1-12 (deprecated, use date instead)
  year?: number // (deprecated, use date instead)
  platform?: StockPlatform
}

export interface GenerationOptions {
  category?: string
  keywordLimit?: number
  includeCompliance?: boolean
  confidenceThreshold?: number
  style?: 'creative' | 'descriptive' | 'commercial'
  context?: ImageContext
}

export type StockPlatform = 'shutterstock' | 'adobe-stock' | 'getty-images' | 'istock' | 'generic'

export interface GenerationProgress {
  batchId: string
  totalImages: number
  processedImages: number
  currentImageId?: string
  status: 'initializing' | 'processing' | 'completed' | 'error'
  error?: string
  estimatedTimeRemaining?: number
}

export interface MetadataUpdate {
  title?: string
  description?: string
  keywords?: string[]
  category?: string
  isCommercial?: boolean
  hasModelRelease?: boolean
  hasPropertyRelease?: boolean
  complianceFlags?: string[]
}

export interface ImageAnalysis {
  dominantColors: string[]
  subjects: string[]
  composition: 'landscape' | 'portrait' | 'square'
  brightness: 'low' | 'medium' | 'high'
  complexity: 'simple' | 'moderate' | 'complex'
  style: 'photography' | 'illustration' | 'graphic'
}

/**
 * Metadata Generator Service Interface
 * All implementations must adhere to this contract
 */
export interface MetadataGenerator {
  /**
   * Generates metadata for a single image
   * @param image - ImageFile object to analyze
   * @param options - Generation options and preferences
   * @returns Promise resolving to generated Metadata
   * @throws GenerationError if processing fails
   */
  generateMetadata(
    image: ImageFile,
    options?: GenerationOptions
  ): Promise<Metadata>

  /**
   * Generates metadata for multiple images in batch
   * @param images - Array of ImageFile objects
   * @param options - Generation options applied to all images
   * @returns Promise resolving to map of imageId to Metadata
   * @throws GenerationError if batch processing fails
   */
  batchGenerate(
    images: ImageFile[],
    options?: GenerationOptions
  ): Promise<Record<string, Metadata>>

  /**
   * Updates existing metadata with user edits
   * @param imageId - Target image identifier
   * @param updates - Partial metadata updates
   * @returns Promise resolving to updated Metadata
   * @throws ValidationError if updates are invalid
   */
  updateMetadata(imageId: string, updates: MetadataUpdate): Promise<Metadata>

  /**
   * Gets current generation progress for a batch
   * @param batchId - Batch identifier
   * @returns Current generation progress
   */
  getGenerationProgress(batchId: string): GenerationProgress

  /**
   * Cancels ongoing generation process
   * @param batchId - Batch identifier
   * @returns Promise resolving when cancellation complete
   */
  cancelGeneration(batchId: string): Promise<void>

  /**
   * Validates metadata against business rules
   * @param metadata - Metadata to validate
   * @param platform - Optional platform to validate categories against
   * @returns Validation result with errors/warnings
   */
  validateMetadata(metadata: Metadata, platform?: string): ValidationResult

  /**
   * Analyzes image for visual characteristics
   * @param image - ImageFile to analyze
   * @returns Promise resolving to image analysis results
   */
  analyzeImage(image: ImageFile): Promise<ImageAnalysis>

  /**
   * Gets available categories for classification
   * @param platform - Optional platform to get platform-specific categories
   * @returns Array of category names
   */
  getAvailableCategories(platform?: string): string[]

  /**
   * Gets suggested keywords for a category
   * @param category - Category name
   * @returns Array of suggested keywords
   */
  getSuggestedKeywords(category: string): string[]

  /**
   * Regenerates metadata with different options
   * @param imageId - Target image identifier
   * @param options - New generation options
   * @returns Promise resolving to regenerated Metadata
   */
  regenerateMetadata(
    imageId: string,
    options: GenerationOptions
  ): Promise<Metadata>
}

/**
 * Error types thrown by MetadataGenerator implementations
 */
export class GenerationError extends Error {
  constructor(
    message: string,
    public code: string,
    public imageId?: string,
    public details?: Record<string, any>,
  ) {
    super(message)
    this.name = 'GenerationError'
  }
}

export class ValidationError extends Error {
  constructor(
    message: string,
    public field: string,
    public value: any,
  ) {
    super(message)
    this.name = 'ValidationError'
  }
}

/**
 * Generation error codes
 */
export const GENERATION_CODES = {
  IMAGE_ANALYSIS_FAILED: 'IMAGE_ANALYSIS_FAILED',
  INVALID_IMAGE_FORMAT: 'INVALID_IMAGE_FORMAT',
  PROCESSING_TIMEOUT: 'PROCESSING_TIMEOUT',
  INSUFFICIENT_QUALITY: 'INSUFFICIENT_QUALITY',
  METADATA_VALIDATION_FAILED: 'METADATA_VALIDATION_FAILED',
  CATEGORY_NOT_SUPPORTED: 'CATEGORY_NOT_SUPPORTED',
  KEYWORD_LIMIT_EXCEEDED: 'KEYWORD_LIMIT_EXCEEDED',
} as const

/**
 * Validation result structure
 */
export interface ValidationResultError {
  field: string
  message: string
  code: string
}

export interface ValidationResult {
  isValid: boolean
  errors: ValidationResultError[]
  warnings: ValidationWarning[]
}

export interface ValidationWarning {
  field: string
  message: string
  suggestion?: string
}

/**
 * Stock photography categories
 */
export const STOCK_CATEGORIES = [
  'Abstract',
  'Animals/Wildlife',
  'Arts',
  'Backgrounds/Textures',
  'Beauty/Fashion',
  'Buildings/Landmarks',
  'Business/Finance',
  'Celebrities',
  'Education',
  'Food and Drink',
  'Healthcare/Medical',
  'Holidays',
  'Industrial',
  'Lifestyle',
  'Nature',
  'People',
  'Religion',
  'Science',
  'Signs/Symbols',
  'Sports/Recreation',
  'Technology',
  'Transportation',
  'Travel',
] as const

export type StockCategory = (typeof STOCK_CATEGORIES)[number]

/**
 * Common compliance flags for stock photography
 */
export const COMPLIANCE_FLAGS = [
  'MODEL_RELEASE_REQUIRED',
  'PROPERTY_RELEASE_REQUIRED',
  'EDITORIAL_USE_ONLY',
  'TRADEMARK_VISIBLE',
  'COPYRIGHT_MATERIAL',
  'SENSITIVE_CONTENT',
  'QUALITY_ISSUES',
  'TECHNICAL_PROBLEMS',
] as const

export type ComplianceFlag = (typeof COMPLIANCE_FLAGS)[number]

/**
 * Platform-specific category mappings based on 2025 research
 */
export const PLATFORM_CATEGORIES = {
  'shutterstock': [
    'Animals/Wildlife',
    'Arts',
    'Backgrounds/Textures',
    'Beauty/Fashion',
    'Buildings/Landmarks',
    'Business/Finance',
    'Celebrities',
    'Education',
    'Food and Drink',
    'Healthcare/Medical',
    'Holidays',
    'Industrial',
    'Interiors',
    'Miscellaneous',
    'Nature',
    'Objects',
    'Parks/Outdoor',
    'People',
    'Religion',
    'Science',
    'Signs/Symbols',
    'Sports/Recreation',
    'Technology',
    'Transportation',
    'Vintage',
  ],
  'adobe-stock': [
    'Animals',
    'Buildings and Architecture',
    'Business',
    'Culture and Religion',
    'Drinks',
    'Food',
    'Graphic Resources',
    'Hobbies and Leisure',
    'Industry',
    'Landscapes',
    'Lifestyle',
    'People',
    'Plants and Flowers',
    'Science',
    'Social Issues',
    'Sports',
    'States of Mind',
    'Technology',
    'The Environment',
    'Transport',
    'Travel',
  ],
  'getty-images': [
    'Abstract Concepts',
    'Animals',
    'Arts Culture and Entertainment',
    'Business',
    'Education',
    'Food and Drink',
    'Government',
    'Healthcare and Medicine',
    'Holidays and Celebrations',
    'Industrial',
    'Lifestyle',
    'Nature',
    'People',
    'Places',
    'Religion',
    'Science',
    'Social Issues',
    'Sports',
    'Technology',
    'Transportation',
    'Travel',
    'War and Conflict',
  ],
  'istock': [
    'Abstract',
    'Animals/Wildlife',
    'Arts',
    'Beauty/Fashion', 
    'Business/Finance',
    'Buildings/Landmarks',
    'Education',
    'Food and Drink',
    'Healthcare/Medical',
    'Holidays',
    'Industrial',
    'Lifestyle',
    'Nature',
    'People',
    'Religion',
    'Science',
    'Sports/Recreation',
    'Technology',
    'Transportation', 
    'Travel',
    'Signs/Symbols',
  ],
  'generic': [
    'Abstract',
    'Animals/Wildlife',
    'Arts',
    'Backgrounds/Textures',
    'Beauty/Fashion',
    'Buildings/Landmarks',
    'Business/Finance',
    'Celebrities',
    'Education',
    'Food and Drink',
    'Healthcare/Medical',
    'Holidays',
    'Industrial',
    'Lifestyle',
    'Nature',
    'People',
    'Religion',
    'Science',
    'Signs/Symbols',
    'Sports/Recreation',
    'Technology',
    'Transportation',
    'Travel',
  ]
} as const

export type PlatformCategory = {
  [K in StockPlatform]: (typeof PLATFORM_CATEGORIES)[K][number]
}
